import { useEffect, useMemo } from "react";
import { Control, useController, useWatch } from "react-hook-form";

import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import type { SxProps } from "@mui/material/styles";

import { GROUP_AUDIOLOGISTS } from "constants/staff";
import { useGetStaffQuery } from "services/staff";
import { MinimalStaffMember, StaffFilters } from "services/types/staff.type";

import { AppointmentFormValues } from "../appointment-form.component";

type StaffFieldProps = {
  control: Control<AppointmentFormValues, object>;
  showActiveOnly?: boolean;
  sx?: SxProps;
};

export default function StaffMembersField({
  control,
  showActiveOnly = false,
  sx
}: StaffFieldProps) {
  const [appointmentType, clinic, isGlobalPracticeEvent] = useWatch({
    control,
    name: ["appointment_type", "clinic", "is_global_practice_event"]
  });

  const staffFilters: StaffFilters = useMemo(
    () => ({
      clinicIds: !isGlobalPracticeEvent && clinic?.id ? [clinic.id] : [],
      groupName: appointmentType?.is_audiologists_only
        ? GROUP_AUDIOLOGISTS
        : undefined,
      is_active: showActiveOnly || null
    }),
    [clinic, appointmentType, showActiveOnly, isGlobalPracticeEvent]
  );

  const { data: staff = [], isLoading } = useGetStaffQuery(staffFilters);
  const {
    field: { onChange, ...restField },
    formState: { errors }
  } = useController({
    name: "staff_members",
    control
  });

  useEffect(() => {
    !isLoading &&
      onChange(
        restField.value?.filter((staffMember) =>
          Boolean(staff?.find(({ id }) => id === staffMember?.id))
        ) ?? []
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staff, isLoading, onChange]);

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={sx}
      >
        <PeopleOutlinedIcon sx={{ mt: 1.5, mr: 1.5 }} color="action" />
        <Stack direction="row" spacing={2} width="100%">
          <Box sx={{ minWidth: "100%" }}>
            <Autocomplete
              disablePortal
              options={staff as MinimalStaffMember[]}
              filterSelectedOptions
              loading={isLoading}
              fullWidth
              multiple
              popupIcon={<ExpandMoreOutlinedIcon />}
              getOptionLabel={({ fullName }) => fullName}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  {...(Boolean(errors?.staff_members?.message)
                    ? {
                        error: true,
                        helperText: errors?.staff_members?.message
                      }
                    : {})}
                  label="Staff Members"
                />
              )}
              onChange={(_, data) => {
                onChange(data);
                restField.onBlur();
              }}
              {...restField}
            />
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
